if (!customElements.get('subscription-popup')) {
  customElements.define(
    'subscription-popup',
    class SubscriptionPopup extends DrawerComponent {
      static get observedAttributes() {
        return super.observedAttributes.concat(['data-trigger-open', 'data-repeat-open']);
      }

      constructor() {
        super();
      }

      get requiresBodyAppended() {
        return false;
      }

      connectedCallback() {
        super.connectedCallback();

        this.triggerOpen = this.dataset.triggerOpen;
        this.repeatOpen = this.dataset.repeatOpen;
        // KORE R2: delay comes from the section setting (seconds); falls back to 12 s
        this.delayMs = (parseInt(this.dataset.delay, 10) || 12) * 1000;
        this.cookieName = 'hypertheme:popup';
        this.triggered = false;

        // KORE R2: "No, thanks" closes the popup the same way the X button does
        this.querySelectorAll('[data-popup-dismiss]').forEach((btn) => {
          btn.addEventListener('click', () => {
            const closeBtn = this.querySelector('.drawer__close-btn');
            if (closeBtn) closeBtn.click();
            else if (typeof this.hide === 'function') this.hide();
          });
        });

        // KORE: the "Get 15% Off" teaser shows only while the popup is closed
        this.addEventListener('toggle', () => {
          if (this.open) this.hideTeaser();
          else this.showTeaser();
        });

        window.FoxTheme.DOMready(this.handleOpen.bind(this));
      }

      get teaser() {
        const btn = document.querySelector('.subscription-popup__teaser[aria-controls="' + this.id + '"]');
        // The tab and its close button share a wrapper; show/hide the wrapper
        return btn ? btn.closest('.kore-teaser-wrap') || btn : null;
      }

      teaserDismissed() {
        try {
          return !!sessionStorage.getItem('kore_teaser_dismissed');
        } catch (e) {
          return false;
        }
      }

      // KORE: the small x under the tab hides it for the rest of this visit
      bindTeaserClose() {
        const wrap = this.teaser;
        const closeBtn = wrap && wrap.querySelector('[data-kore-teaser-close]');
        if (!closeBtn || closeBtn.dataset.bound) return;
        closeBtn.dataset.bound = 'true';
        closeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          try {
            sessionStorage.setItem('kore_teaser_dismissed', '1');
          } catch (err) {}
          this.hideTeaser();
        });
      }

      showTeaser() {
        if (this.teaserDismissed()) return;
        if (this.teaser) this.teaser.classList.add('is-visible');
      }

      hideTeaser() {
        if (this.teaser) this.teaser.classList.remove('is-visible');
      }

      prepareToShow() {
        super.prepareToShow();
        this.setRepeatOpen();
      }

      // KORE R2: open once per page, whichever trigger fires first
      openOnce() {
        if (this.triggered || !this.isConnected) return;
        this.triggered = true;
        this.show();
      }

      isSubscribed() {
        // Set by the inline script in the section after a successful signup or an email click
        try {
          if (localStorage.getItem('kore_newsletter_subscribed')) return true;
          if (sessionStorage.getItem('kore_newsletter_subscribed')) return true;
        } catch (e) {}
        return false;
      }

      handleOpen() {
        this.bindTeaserClose();
        if (Shopify.designMode) return;
        if (this.isSubscribed()) return;

        let savedData = FoxTheme.utils.getStorage(this.cookieName);
        if (savedData && savedData.opened) {
          // Popup already seen recently, so it won't open this visit: show the teaser instead
          this.showTeaser();
          return false;
        }

        switch (this.triggerOpen) {
          case 'delay_exit':
            // KORE R2: after the delay, or earlier on exit intent (desktop only)
            setTimeout(() => this.openOnce(), this.delayMs);
            this.triggerOnExitIntent(() => this.openOnce());
            break;
          case 'delay':
            setTimeout(() => this.openOnce(), this.delayMs);
            break;
          case 'scroll_down':
            this.triggerAfterScroll(700, () => this.openOnce());
            break;
          default:
            this.openOnce();
            break;
        }
      }

      // KORE R2: cursor leaves through the top of the window (toward tabs / address bar).
      // Phones have no cursor, so they rely on the delay. Ignored for the first 3 s on the page.
      triggerOnExitIntent(trigger) {
        const hasMouse = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
        if (!hasMouse) return;

        const armedAt = Date.now() + 3000;
        const onMouseOut = (e) => {
          if (Date.now() < armedAt) return;
          if (e.relatedTarget || e.toElement) return;
          if (e.clientY > 10) return;
          document.removeEventListener('mouseout', onMouseOut);
          trigger();
        };
        document.addEventListener('mouseout', onMouseOut);
      }

      triggerAfterScroll(offset = 500, trigger) {
        let opened = false;
        const start = 0;
        const onScroll = () => {
          let scrollTop =
            window.pageYOffset || (document.documentElement || document.body.parentNode || document.body).scrollTop;

          if (scrollTop - start > offset && !opened) {
            opened = true;
            window.removeEventListener('scroll', onScroll);
            trigger();
          }
        };
        window.addEventListener('scroll', onScroll, { passive: true });
      }

      setRepeatOpen() {
        if (Shopify.designMode) return;

        let expires;
        switch (this.repeatOpen) {
          case 'no_repeat': expires = 365; break;
          case 'every_3_mins': expires = 3 / 60 / 24; break;
          case 'every_5_mins': expires = 5 / 60 / 24; break;
          case 'every_10_mins': expires = 1 / 6 / 24; break;
          case 'every_15_mins': expires = 15 / 60 / 24; break;
          case 'every_30_mins': expires = 1 / 2 / 24; break;
          case 'every_1_hr': expires = 1 / 24; break;
          case 'every_6_hrs': expires = 6 / 24; break;
          case 'every_12_hrs': expires = 1 / 2; break;
          case 'every_day': expires = 1; break;
          case 'every_3_days': expires = 3; break;
          case 'every_week': expires = 7; break;
          case 'every_2_weeks': expires = 14; break;
          case 'every_month': expires = 30; break;
          default: expires = 7; break;
        }

        window.FoxTheme.utils.setStorage(this.cookieName, { opened: true }, expires);
      }
    }
  );
}